import type { SettingsVersionQuery } from '@paintscout/api'
import { SettingsVersionDocument } from '@paintscout/api'
import type { DocumentNode } from 'graphql'
import type { OptionsDocument, RatesDocument } from 'paintscout'
import type { CurrentSettingsVersion } from './isSettingsOutdated'

/** The part of the Apollo cache this needs (`DataProxy` from `apollo-cache`, not a dependency here). */
export interface SettingsVersionCache {
  readQuery<T>(options: { query: DocumentNode }): T | null
  writeQuery<T>(options: { query: DocumentNode; data: T }): void
}

/**
 * Brings the cached `settingsVersion` result up to the docs this tab just saved or synced (ENG-2701).
 *
 * `useSettingsOutdated` compares `appDocs` with the last `settingsVersion` check. A save writes the
 * server's new `settingsUpdated` stamp and `$rates` `_rev` into `appDocs`, but the last check still
 * holds the values from before the save, so the saving tab flagged itself until the next poll.
 *
 * Does nothing when the query has no cached result (it has not run yet). A doc that is missing, or
 * that has no stamp or rev, keeps the cached value.
 */
export function syncSettingsVersionCache(
  cache: SettingsVersionCache,
  docs: { options?: Partial<OptionsDocument> | null; rates?: Partial<RatesDocument> | null }
): void {
  let cached: SettingsVersionQuery | null = null
  try {
    cached = cache.readQuery<SettingsVersionQuery>({ query: SettingsVersionDocument })
  } catch {
    // The query has not run in this session, so there is nothing to bring up to date.
  }

  // `settingsVersion` is a JSON scalar, so its shape comes from the resolver, not the schema.
  const previous = cached?.settingsVersion as CurrentSettingsVersion | null | undefined
  if (!previous) {
    return
  }

  cache.writeQuery<SettingsVersionQuery>({
    query: SettingsVersionDocument,
    data: {
      ...cached,
      settingsVersion: {
        ...previous,
        options: docs.options?.settingsUpdated ?? previous.options,
        ratesRev: docs.rates?._rev ?? previous.ratesRev
      }
    }
  })
}
